// src/pages/movimentacao-financeiro/MovimentacaoFinanceiroRegistrationPage.jsx
import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import moment from 'moment';
import QCXRegistrationFormPageTemplate
  from '../../../../templates/registration-form-page/QCXRegistrationFormPageTemplate';
import MovimentacaoFinanceiroForm from '../../../../components/movimentacao-financeiro/MovimentacaoFinanceiroForm';
import {
  createAsync, updateAsync, fetchByIdAsync,
  selectMovimentacao, selectMode,
  changeToCreateMode, changeToConsultMode, resetModel, selectStatus, selectError,
} from '../../../../features/movimentacao-financeiro/movimentacaoFinanceiroSlice';
import { setSuccessFeedback, setErrorFeedback } from '../../../../features/feedback/feedbackSlice';
import { useGetUserEmail } from '../../../../ts/common/hooks/keycloak/useGetUserEmail';

export default function MovimentacaoFinanceiroRegistrationPage({ authInfo = {} }) {
  const { id, followUpId } = useParams();
  const dispatch = useDispatch();
  const history = useHistory();
  const movimentacao = useSelector(selectMovimentacao);
  const mode = useSelector(selectMode);
  const usuarioLogado = useGetUserEmail();

  const nowIso = useMemo(() => moment().toISOString(), []);

  const initialValues = useMemo(() => (
    id ? unnormalizeMovimentacao(movimentacao) : {
      insertionDate: nowIso,
      dataOperacao: nowIso,
      followUp: followUpId ? { id: Number(followUpId) } : null,
      contaComissaria: false,
      active: true,
      nomeUsuario: usuarioLogado,
    }
  ), [id, movimentacao, followUpId, nowIso]);

  /* -------- lifecycle: se tiver id => carrega e vai para consult -------- */
  useEffect(() => {
    if (id) {
      dispatch(fetchByIdAsync(id));
      dispatch(changeToConsultMode());
    } else {
      dispatch(changeToCreateMode());
    }
  }, [id, dispatch]);


  const normalizeMovimentacao = (form) => {
    const valor = Number(String(form.valor ?? '')
      .replace(/\./g, '')
      .replace(',', '.'));

    const tipo = form.tipo
      ?? (form.despesaReceita?.tipo === 'DESPESA' ? 'PAGAR' : 'RECEBER');

    return {
      origem: 'MANUAL',
      nomeUsuario: usuarioLogado ?? 'API',
      followUpId: form.followUp?.id
        ? form.followUp?.id :
        null,
      dataOperacao: moment(form.dataOperacao).toISOString(),
      valor,
      tipo,
      contaBancariaId: form.contaBancaria?.id?.id ?? null,
      despesaReceitaId: form.despesaReceita?.id ?? null,
      fornecedorId: form.fornecedor?.id ?? null,
      contaComissaria: !!form.contaComissaria,
      active: true,
    };
  };

  /* Se precisar converter resposta do back‑end para o formulário ---------- */
  const unnormalizeMovimentacao = (api) => ({
    ...api,
    contaBancaria: api.contaBancariaId ? { id: api.contaBancariaId } : undefined,
    despesaReceita: api.despesaReceitaId ? { id: api.despesaReceitaId } : undefined,
    fornecedor: api.fornecedorId ? { id: api.fornecedorId } : undefined,
    valor: api.valor?.toLocaleString('pt-BR', { minimumFractionDigits: 2 }),
    followUp: api.followUpId ? { id: api.followUpId } : undefined,
  });

  const handleSubmit = async (formValues) => {
    const payload = normalizeMovimentacao({
      ...formValues,
      nomeUsuario: authInfo.nome,
    });

    try {
      const result = id
        ? await dispatch(updateAsync({ ...payload, id }))
        : await dispatch(createAsync(payload));

      if (createAsync.fulfilled.match(result) || updateAsync.fulfilled.match(result)) {
        dispatch(setSuccessFeedback({ message: 'Lançamento salvo com sucesso!' }));
        history.push(`/financeiro/conta-corrente-processo/${followUpId}`);
      } else {
        throw new Error(result.payload || 'Erro ao salvar');
      }
    } catch (e) {
      dispatch(setErrorFeedback({ message: e.message || 'Erro ao salvar' }));
    }
  };

  /* -------- breadcrumbs / título -------- */
  const pageTitle = id ? 'Detalhar Lançamento' : 'Novo Lançamento Manual';

  return (
    <QCXRegistrationFormPageTemplate
      pageTitle={pageTitle}
      breadcrumbs={[
        { link: { to: '/', name: 'Início' } },
        { link: { to: '/financeiro/conta-corrente-processo', name: 'Conta Corrente Processo' } },
        { text: { name: id ? 'Detalhar' : 'Novo' } },
      ]}
      isCreate={!id}
      isConsult={!!id}
      isUpdate={!!id}
      authInfo={authInfo}
    >
      {(formProps) => (
        <MovimentacaoFinanceiroForm
          initialValues={initialValues}
          handleSubmit={handleSubmit}
          isConsult={!!id}
          isUpdate={false}
          authInfo={authInfo}
          {...formProps}
        />
      )}
    </QCXRegistrationFormPageTemplate>
  );
}
